import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminOrder } from '../../mocks/adminOrders';

interface NewOrderToastProps {
  orders: AdminOrder[];
  onDismiss: () => void;
}

const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;
const AUTO_DISMISS_MS = 9000;

export default function NewOrderToast({ orders, onDismiss }: NewOrderToastProps) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(100);
  const [leaving, setLeaving] = useState(false);
  const first = orders[0];

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(onDismiss, 280);
  }, [onDismiss]);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct <= 0) {
        clearInterval(tick);
        dismiss();
      }
    }, 80);
    return () => clearInterval(tick);
  }, [dismiss]);

  const handleView = () => {
    dismiss();
    setTimeout(() => navigate('/admin/payments'), 100);
  };

  return (
    <div
      className={`w-80 bg-white rounded-2xl border border-stone-200 overflow-hidden ${
        leaving ? 'animate-slide-out-right' : 'animate-slide-in-right'
      }`}
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
    >
      {/* Timer bar */}
      <div className="h-1 bg-stone-100">
        <div
          className="h-full bg-orange-500 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 animate-bounce">
              <i className="ri-notification-3-fill text-orange-500 text-base" />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900 leading-tight">
                {orders.length === 1 ? 'New order arrived!' : `${orders.length} new orders!`}
              </p>
              <p className="text-xs text-stone-400 mt-0.5">Waiting for confirmation</p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer transition text-stone-400 flex-shrink-0"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Order card */}
        <div className="bg-stone-50 rounded-xl px-3 py-2.5 mb-3 border border-stone-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-stone-700">{first.id}</span>
            <span className="text-xs font-bold text-orange-600">{formatPrice(first.total)}</span>
          </div>
          <p className="text-xs font-medium text-stone-600 truncate">{first.customer}</p>
          <p className="text-xs text-stone-400 truncate mt-0.5">
            <i className="ri-map-pin-line mr-0.5" />
            {first.address}
          </p>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-500">
              <i className="ri-money-dollar-circle-line" />
              {first.paymentMethod}
            </span>
            {first.paymentMethod === 'Cash on Delivery' &&
              first.changeAmount !== undefined &&
              first.changeAmount >= 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                  <i className="ri-coins-line" />
                  Troco: {formatPrice(first.changeAmount)}
                </span>
              )}
          </div>
          {orders.length > 1 && (
            <p className="text-xs text-stone-400 mt-2 font-medium border-t border-stone-100 pt-1.5">
              +{orders.length - 1} more order{orders.length > 2 ? 's' : ''} pending
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={dismiss}
            className="flex-1 py-2 text-xs font-semibold text-stone-500 hover:text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 cursor-pointer transition whitespace-nowrap"
          >
            Dismiss
          </button>
          <button
            onClick={handleView}
            className="flex-1 py-2 text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg cursor-pointer transition whitespace-nowrap flex items-center justify-center gap-1"
          >
            <i className="ri-eye-line text-xs" />
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
}
